import { typeWireOf } from "@typewirets/core";
import { api } from "@/lib/api";
import type {
  AIFieldDefinition,
  CreateAIFieldRequest,
  CreateReportRequest,
  CreateTasksResponse,
  DeepResearchReportMetadata,
  DeepResearchReportSummary,
  DeleteReportResponse,
  FilterGroup,
  ListAIFieldsResponse,
  PatchRowFiltersOptions,
  PatchRowFiltersResponse,
  PopulateRecordsResponse,
  UpdateAIFieldRequest,
  UpdateReportRequest,
} from "@/types/deep-research-reports";
import type { DeepResearchReportsAPI } from "./deep-research-reports.api";

export const DeepResearchReportsAPIWire = typeWireOf({
  token: "DeepResearchReportsAPI",
  createWith(): DeepResearchReportsAPI {
    const impl: DeepResearchReportsAPI = {
      // Reports
      listReports(): Promise<DeepResearchReportSummary[]> {
        return api.get<DeepResearchReportSummary[]>(`deep-research/`);
      },
      createReport(
        body: CreateReportRequest,
      ): Promise<DeepResearchReportMetadata> {
        return api.post<DeepResearchReportMetadata>(`deep-research/`, body);
      },
      fetchMetadata(report_id: string): Promise<DeepResearchReportMetadata> {
        return api.get<DeepResearchReportMetadata>(
          `deep-research/${report_id}/metadata/`,
        );
      },
      fetchReport(report_id: string): Promise<DeepResearchReportMetadata> {
        return api.get<DeepResearchReportMetadata>(
          `deep-research/${report_id}/metadata/`,
        );
      },
      fetchRowDetails(report_id: string, entity_id: string) {
        return api.get(`deep-research/${report_id}/rows/${entity_id}/`);
      },
      updateReport(
        report_id: string,
        body: UpdateReportRequest,
      ): Promise<DeepResearchReportMetadata> {
        return api.patch<DeepResearchReportMetadata>(
          `deep-research/${report_id}/`,
          body,
        );
      },
      populateRecordsList(report_id: string): Promise<PopulateRecordsResponse> {
        return api.post<PopulateRecordsResponse>(
          `deep-research/${report_id}/populate-records-list/`,
          {},
        );
      },
      createTasks(report_id: string): Promise<CreateTasksResponse> {
        return api.post<CreateTasksResponse>(
          `deep-research/${report_id}/create-tasks/`,
          {},
        );
      },
      deleteReport(report_id: string): Promise<DeleteReportResponse> {
        return api.delete<DeleteReportResponse>(`deep-research/${report_id}/`);
      },

      // Data rows with filter/sort support
      fetchReportData(
        report_id: string,
        args?: {
          limit?: number;
          offset?: number;
          sort_by?: string;
          sort_direction?: "asc" | "desc";
          null_sort?: string;
          search?: string;
          filters?: string;
          extra_fields?: string;
        },
      ) {
        const paramsObj: Record<string, string | undefined> = {
          limit: args?.limit != null ? String(args.limit) : undefined,
          offset: args?.offset != null ? String(args.offset) : undefined,
          sort_by: args?.sort_by,
          sort_direction: args?.sort_direction,
          null_sort: args?.null_sort,
          search: args?.search,
          filters: args?.filters,
          extra_fields: args?.extra_fields,
        };
        const params: Record<string, string> = Object.fromEntries(
          Object.entries(paramsObj).filter(([, v]) => v !== undefined) as [
            string,
            string,
          ][],
        );
        return api.get<{
          results: Record<string, unknown>[];
          total: number;
          limit: number;
          offset: number;
        }>(`deep-research/${report_id}/`, { params });
      },

      // Row filters
      patchRowFilters(
        report_id: string,
        args: { row_filters: FilterGroup; options?: PatchRowFiltersOptions },
      ): Promise<PatchRowFiltersResponse> {
        const { row_filters, options } = args;
        return api.patch<PatchRowFiltersResponse>(
          `deep-research/${report_id}/row-filters/`,
          { row_filters, options },
        );
      },

      // AI Fields
      async listAIFields(entityType: string): Promise<AIFieldDefinition[]> {
        const params: Record<string, string> = {
          entity_type: entityType,
        };
        const r = await api.get<{ ai_fields: AIFieldDefinition[] }>(
          `deep-research/ai-fields/`,
          { params },
        );
        return r.ai_fields;
      },
      async listAllAIFields(
        filters?: Record<string, unknown>,
      ): Promise<ListAIFieldsResponse> {
        // Build params with standard filter format
        const params: Record<string, string> = {};

        // Add search if provided
        if (filters?.search) {
          params.search = String(filters.search);
        }

        // Add sorting if provided
        if (filters?.sort_by) {
          params.sort_by = String(filters.sort_by);
        }
        if (filters?.sort_direction) {
          params.sort_direction = String(filters.sort_direction);
        }

        // Add pagination if provided
        if (filters?.limit) {
          params.limit = String(filters.limit);
        }
        if (filters?.offset) {
          params.offset = String(filters.offset);
        }

        // Add filters if provided (standard JSON format)
        if (filters?.filters) {
          params.filters =
            typeof filters.filters === "string"
              ? filters.filters
              : JSON.stringify(filters.filters);
        }

        const r = await api.get<ListAIFieldsResponse>(
          `deep-research/ai-fields/list/`,
          { params },
        );
        return r;
      },
      createAIField(body: CreateAIFieldRequest): Promise<AIFieldDefinition> {
        return api.post<AIFieldDefinition>(`deep-research/ai-fields/`, body);
      },
      updateAIField(
        field_id: string,
        body: UpdateAIFieldRequest,
      ): Promise<AIFieldDefinition> {
        return api.patch<AIFieldDefinition>(
          `deep-research/ai-fields/${field_id}/`,
          body,
        );
      },
      async deleteAIField(field_id: string): Promise<void> {
        await api.delete<void>(`deep-research/ai-fields/${field_id}/delete`);
      },
    };

    return impl;
  },
});
